import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Heart, Loader2, Shield, ShieldOff, RefreshCw } from 'lucide-react';

const ROLE_BADGE = {
  administrator: 'bg-[#EDE7F6] text-[#5A2F8E] border-[#9B7ED8]',
  supervisor: 'bg-[#E1F5FE] text-[#0277BD] border-[#81D4FA]',
  scheduler: 'bg-[#FFF8E1] text-[#9A6D00] border-[#FFD54F]',
};

export default function DonorCrmAccessManager({ api }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/donor-crm-access');
      setUsers(data || []);
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Administrators only' : 'Failed to load access list');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggle = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.put(`/admin/donor-crm-access/${u.id}`, { grant: !u.has_access });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, has_access: data.has_access } : x));
      toast.success(`${data.name} ${data.has_access ? 'granted' : 'revoked'} Donor CRM access`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setBusyId(null);
  };

  if (loading) return <Card><CardContent className="p-12 text-center"><Loader2 className="h-7 w-7 animate-spin text-[#B71C5C] mx-auto" /></CardContent></Card>;

  const granted = users.filter(u => u.has_access);
  const revoked = users.filter(u => !u.has_access);

  return (
    <Card data-testid="donor-access-manager">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-[#FCE4EC]">
              <Heart className="h-5 w-5 text-[#B71C5C]" fill="#B71C5C" />
            </div>
            <div>
              <CardTitle className="text-base font-bold">Donor CRM — access</CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                Only granted users can view or edit donor records. Everyone else won't see the Donors menu item at all.
              </p>
            </div>
          </div>
          <Button onClick={fetchUsers} variant="outline" size="sm" className="gap-1.5" data-testid="donor-access-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-gray-600">
          {granted.length} member{granted.length === 1 ? '' : 's'} · {revoked.length} eligible (not yet granted)
        </div>

        {granted.length > 0 && (
          <div className="border border-[#F8BBD0] bg-[#FCE4EC] rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-[#F48FB1] text-white text-[10px] uppercase tracking-wider font-bold">Has access</div>
            {granted.map(u => <Row key={u.id} u={u} onToggle={toggle} busy={busyId === u.id} granted />)}
          </div>
        )}

        <div className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 text-[10px] uppercase tracking-wider font-bold text-gray-600">
            Eligible — not granted
          </div>
          {revoked.length === 0
            ? <p className="text-xs italic text-gray-500 p-4 text-center">All eligible users already have access.</p>
            : revoked.map(u => <Row key={u.id} u={u} onToggle={toggle} busy={busyId === u.id} />)}
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ u, onToggle, busy, granted }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-white/60"
         data-testid={`donor-access-row-${u.id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
           style={{ background: 'linear-gradient(135deg, #B71C5C, #F48FB1)', color: 'white' }}>
        {(u.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold truncate">{u.name || '(no name)'}</span>
          <Badge className={`text-[9px] border ${ROLE_BADGE[u.role] || ''}`}>{u.role}</Badge>
        </div>
        <p className="text-[11px] text-gray-500 truncate">{u.email}</p>
      </div>
      <Button size="sm" onClick={() => onToggle(u)} disabled={busy}
              variant={granted ? 'outline' : 'default'}
              className={granted ? 'border-red-300 text-red-600 hover:bg-red-50 gap-1.5 h-8'
                                 : 'bg-[#B71C5C] hover:bg-[#8E1249] text-white gap-1.5 h-8'}
              data-testid={`donor-access-toggle-${u.id}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : granted ? <ShieldOff className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
        {granted ? 'Revoke' : 'Grant access'}
      </Button>
    </div>
  );
}
